import {inject, Injectable} from '@angular/core';
import {forkJoin, merge, Observable, of} from 'rxjs';
import {concatMap, finalize, map, switchMap, tap} from 'rxjs/operators';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialogRef} from '@angular/material/dialog';
import {DialogService, ISelectFormOption} from 'frontier/browserkit';
import {ControlStore, dateObjectToISOString, EControlActions, IControlObject} from 'frontier/nucleus';
import {CartListService} from 'src/app/api/generated';
import {NewMissionDialogComponent} from './new-mission-dialog/new-mission-dialog.component';
import {INewMissionDialog} from './new-mission-dialog/new-mission-dialog.interface';
import AppControlGuids from '../../../../api/ControlGuids';

@Injectable({providedIn: 'root'})
export class CartCreationService {
  private _cartListService: CartListService = inject(CartListService);
  private _dialogService: DialogService = inject(DialogService);
  private _snackBar: MatSnackBar = inject(MatSnackBar);
  private _controlStore: ControlStore = inject(ControlStore);

  createMission(instanceId: string | null, options?: { onFinalize?: () => void }): Observable<any> {
    return forkJoin({
      businessAreas: this._cartListService.cartListGetBusinessAreas(),
      ambulanceStations: this._cartListService.cartListGetLocations(),
    }).pipe(
      switchMap(({businessAreas, ambulanceStations}:
                 { businessAreas: IControlObject[], ambulanceStations: IControlObject[] }): Observable<any> => {
        return this.openCreateMissionDialog(instanceId, businessAreas, ambulanceStations, options?.onFinalize);
      })
    );
  }

  private openCreateMissionDialog(
    instanceId: string | null,
    businessAreas: IControlObject[],
    ambulanceStations: IControlObject[],
    onFinalize?: () => void,
  ): Observable<any> {
    const dialogRef: MatDialogRef<NewMissionDialogComponent> =
      this._dialogService.dialog.open(NewMissionDialogComponent, {
        width: '32rem',
        data: {
          instanceId,
          edit: false,
          businessAreas: businessAreas.map((b: IControlObject): ISelectFormOption => ({
            name: b.name,
            value: b.signature,
          })),
          ambulanceStations: ambulanceStations.map((a: IControlObject): ISelectFormOption => ({
            name: a.name,
            value: a.signature,
          })),
        } as INewMissionDialog,
      });
    return merge(
      dialogRef.afterClosed().pipe(
        finalize((): void => {
          onFinalize?.();
        }),
      ),
      dialogRef.componentInstance.createAdditional,
    ).pipe(
      concatMap((result: INewMissionDialog): Observable<any> => {
        if (result) {
          return this._cartListService.cartListNewCart({
            number: result.missionNumber,
            date: dateObjectToISOString(result.date),
            businessarea: businessAreas.find((ba: IControlObject): boolean =>
              ba.signature === result.businessAreaSignature),
            station: ambulanceStations.find((as: IControlObject): boolean =>
              as.signature === result.ambulanceStationSignature),
          }).pipe(
            tap((res: any): void => {
              this._controlStore.controlDataChanged$.emit({
                changeType: EControlActions.create,
                GUID: AppControlGuids.TCartList,
              });

              if (res) {
                this._snackBar.open('Ein neuer Einsatz wurde angelegt.', 'ok',
                  {duration: 3000, verticalPosition: 'top'});
              }
            }),
            map((res: any) => res ? {...res, number: result.missionNumber} : res),
          );
        }
        return of(null);
      }),
    );
  }
}
